import React, { useEffect, useState } from 'react'
import Layout from '../component/layout'
import { Link, useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { getItem } from "../../../utils/localStorage";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import { getCustomerInvoicesURL } from "../../../utils/urls";
import { filterObjectsByAllProperties } from '../../../utils/filterSearch';

const ExpenseReports = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState('All');
  const [filteredInvoices, setFilteredInvoices] = useState([]);


  const handleStatusFilter = (e) => {
    const selected = e.target.value;
    setStatusFilter(selected);

    if (selected === 'All') {
      setFilteredInvoices(invoices);
    } else {
      const filtered = invoices.filter(inv => inv.status === selected);
      setFilteredInvoices(filtered);
    }
  };

  const fetchInvoices = async () => {
    try {
      const facilityId = await getItem('selectedFacilityId');
      const customerId = await getItem('selectedCustomerId');

      const response = await makeRequest2(
        `${getCustomerInvoicesURL}/${facilityId}/${customerId}`,
        "GET",
        {}
      );

      console.log("Fetched Invoices: ", response.data.data.invoices);

      if (response.success && response.data?.data) {
        const fetchedInvoices = response.data.data.invoices || [];

        // Filter invoices to only include those with invoiceType 'Contract'
        const contractInvoices = fetchedInvoices.filter(invoice =>
          invoice.whatFor && invoice.whatFor.invoiceType === 'Contract'
        );

        setInvoices(contractInvoices); // Set only 'Contract' invoices
        setFilteredInvoices(contractInvoices); // Set filtered invoices to 'Contract' invoices
        setSummary(response.data.data.summary);
      } else {
        toastify("Failed to fetch invoices", 'error');
      }
    } catch (err) {
      toastify(err.message, 'error');
    }
  };

  const actionTemplate = (rowData) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="ti ti-eye"
          className="p-button-rounded p-button-success p-ml-2"
          onClick={(e) => {
            e.stopPropagation();
            handleViewInvoice(rowData._id);
          }}
          tooltip="View Details"
        />
      </div>
    );
  };

  const handleViewInvoice = (invoiceId) => {
    navigate(`/portal/view_invoice/${invoiceId}`);
  };

  const statusTemplate = (rowData) => {
    const statusStyles = {
      Paid: { color: '#28a745', backgroundColor: '#e9fbe9' },
      Pending: { color: '#ffc107', backgroundColor: '#fff8e1' },
      Overdue: { color: '#dc3545', backgroundColor: '#fde9e9' }
    }

    const style = statusStyles[rowData.status] || {
      color: '#6c757d',
      backgroundColor: '#f1f1f1'
    }

    return (
      <span
        style={{
          ...style,
          padding: '0.5em 1em',
          borderRadius: '12px',
          fontWeight: 'bold',
          fontSize: '0.9rem',
          display: 'inline-block'
        }}
      >
        {rowData.status}
      </span>
    )
  }

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount, currencyCode = 'KES') => {
    if (typeof amount !== 'number') return `${currencyCode} 0.00`;
    return `${currencyCode} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  // Get currency code for individual invoice
  const getInvoiceCurrency = (invoice) => {
    // Try to get currency from the invoice object if it exists
    if (invoice.currency && invoice.currency.code) {
      return invoice.currency.code;
    }

    // Default to KES if no currency indicator is found
    return 'KES';
  };

  const handleInputSearch = async (value) => {
    try {
      setSearchTerm(value);
      if (value === '') {
        fetchInvoices()
      } else {
        const results = await filterObjectsByAllProperties(filteredInvoices, value);
        setFilteredInvoices(results);
      }
    } catch (error) {
      toastify(error.message, 'error');
    }
  };

  const getTotalAmount = (invoices) =>
    invoices.reduce((acc, inv) => acc + (inv.totalAmount || 0), 0);

  const getTotalPaid = (invoices) =>
    invoices.reduce((acc, inv) => acc + (inv.status === 'Paid' ? inv.totalAmount || 0 : 0), 0);

  const getTotalUnpaid = (invoices) =>
    invoices.reduce((acc, inv) => acc + (inv.status === 'Unpaid' || inv.status === 'Pending' ? inv.totalAmount || 0 : 0), 0);

  const getTotalOverdue = (invoices) =>
    invoices.reduce((acc, inv) => acc + (inv.status === 'Overdue' ? inv.totalAmount || 0 : 0), 0);


  useEffect(() => {
    fetchInvoices();
  }, []);


  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to={"/landlord_portal"}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={"#"}>Key Metrics</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={"/portal/metrics/revenue_reports"}>Revenue Reports</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="row">
            <div className="col-sm-12">

              <div className="row">
                <div className="col-sm-12">
                  <div className="card">
                    <div className="card-body">
                      <div className="container mt-4">
                        <h4 className='mb-4'>Levy Invoices</h4>

                        <div className="row mb-4">
                          <div className="col-md-3">
                            <div className="p-3" style={{ background: 'transparent', border: '1px solid #ccc', borderRadius: '8px' }}>
                              <h5 className="text-primary">Total to be Paid</h5>
                              <h4 className="fw-bold text-primary">
                                {formatCurrency(getTotalAmount(filteredInvoices), 'KES')}
                              </h4>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="p-3" style={{ background: 'transparent', border: '1px solid #ccc', borderRadius: '8px' }}>
                              <h5 className="text-success">Total Paid</h5>
                              <h4 className="fw-bold text-success">
                                {formatCurrency(getTotalPaid(filteredInvoices), 'KES')}
                              </h4>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="p-3" style={{ background: 'transparent', border: '1px solid #ccc', borderRadius: '8px' }}>
                              <h5 className="text-danger">Total Unpaid</h5>
                              <h4 className="fw-bold text-danger">
                                {formatCurrency(getTotalUnpaid(filteredInvoices), 'KES')}
                              </h4>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="p-3" style={{ background: 'transparent', border: '1px solid #ccc', borderRadius: '8px' }}>
                              <h5 className="text-warning">Total Overdue</h5>
                              <h4 className="fw-bold text-warning">
                                {formatCurrency(getTotalOverdue(filteredInvoices), 'KES')}
                              </h4>
                            </div>
                          </div>
                        </div>




                        <div className="row">
                          <div className="col-md-12">
                            <div className="card">
                              <div className="card-body">


                                <div className="d-flex align-items-center justify-content-between mb-3">

                                  <input
                                    type="text"
                                    className="form-control"
                                    style={{ maxWidth: "250px" }}
                                    placeholder="Search Invoice #"
                                    value={searchTerm}
                                    onChange={(e) => handleInputSearch(e.target.value)}
                                  />

                                  <div className="d-flex align-items-center ms-auto">
                                    <label className="fw-bold me-2">Filter by Status:</label>
                                    <select
                                      className="form-control"
                                      style={{ width: "250px" }}
                                      value={statusFilter}
                                      onChange={handleStatusFilter}
                                    >
                                      {["All", "Unpaid", "Pending", "Paid", "Overdue", "Cancelled", "Partially Paid"].map(status => (
                                        <option key={status} value={status}>{status}</option>
                                      ))}
                                    </select>
                                  </div>
                                </div>

                                <DataTable
                                  value={filteredInvoices}
                                  emptyMessage="No invoices found."
                                  sortMode="multiple"
                                  paginator
                                  rows={5}
                                  stripedRows
                                  tableStyle={{ minWidth: '50rem' }}
                                >
                                  <Column field="invoiceNumber" header="Invoice Number" sortable />

                                  <Column
                                    field="totalAmount"
                                    header="Amount"
                                    style={{ minWidth: "120px" }}
                                    body={(rowData) => {
                                      const currencyCode = getInvoiceCurrency(rowData);
                                      return formatCurrency(rowData.totalAmount, currencyCode);
                                    }}
                                    sortable
                                  />
                                  <Column
                                    field="issueDate"
                                    header="Issue Date"
                                    sortable
                                    body={(rowData) => formatDate(rowData.issueDate)}
                                  />
                                  <Column
                                    field="dueDate"
                                    header="Due Date"
                                    sortable
                                    body={(rowData) => formatDate(rowData.dueDate)}
                                  />
                                  <Column
                                    field="status"
                                    header="Status"
                                    sortable
                                    body={statusTemplate}
                                  />
                                  <Column
                                    field="actions"
                                    header="Actions"
                                    body={actionTemplate}
                                  />
                                </DataTable>



                              </div>
                            </div>
                          </div>
                        </div>



                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

    </Layout>
  )
}

export default ExpenseReports